export type RegisteredKey = {
  context: string;
  key: string;
};

export type RegisteredKeyList = RegisteredKey[];

export abstract class AbstractStringStore {
  constructor(readonly indexKey: string) {}

  #deserializeKeyList(serializedList: string): RegisteredKeyList {
    return serializedList.split(',').map((s) => {
      const [context, key] = s.split(':');
      return {
        context,
        key,
      };
    });
  }

  #getKeyList(): RegisteredKeyList {
    const serializedList = this.retrieve(this.indexKey, '/');

    if (serializedList) {
      return this.#deserializeKeyList(serializedList);
    }

    return [];
  }

  #findRegisteredKey(
    list: RegisteredKeyList,
    searchKey: string,
    searchContext: string
  ) {
    return list.find(
      (keyInfo) =>
        keyInfo.key === searchKey && keyInfo.context === searchContext
    );
  }

  #registerKey(key: string, context: string) {
    const list = this.#getKeyList();

    if (this.#findRegisteredKey(list, key, context) == null) {
      list.push({
        key,
        context,
      });

      this.storeImpl(this.indexKey, this.#serializeKeyList(list), '/');
    }
  }

  #serializeKeyList(list: RegisteredKeyList): string {
    return list.map((k) => `${k.context}:${k.key}`).join(',');
  }

  #unregisterKey(key: string, context: string) {
    const list = this.#getKeyList();

    const cleanedList = list.filter(
      (keyInfo) => keyInfo.key !== key || keyInfo.context !== context
    );

    if (cleanedList.length === 0) {
      this.removeImpl(this.indexKey, '/');
    } else if (list.length !== cleanedList.length) {
      this.storeImpl(this.indexKey, this.#serializeKeyList(cleanedList), '/');
    }
  }

  protected exists(key: string, context: string): boolean {
    return this.#findRegisteredKey(this.#getKeyList(), key, context) != null;
  }

  protected abstract clearImpl(list: RegisteredKeyList): void;
  protected abstract removeImpl(key: string, context: string): void;
  protected abstract retrieveImpl(
    key: string,
    context: string
  ): string | undefined;
  protected abstract storeImpl(
    key: string,
    value: string,
    context: string
  ): void;

  clear(): void {
    this.clearImpl(this.#getKeyList());
    this.removeImpl(this.indexKey, '/');
  }

  remove(key: string, context = '/') {
    this.removeImpl(key, context);
    this.#unregisterKey(key, context);
  }

  store(key: string, value: string, context = '/'): void {
    this.storeImpl(key, value, context);
    this.#registerKey(key, context);
  }

  retrieve(key: string, context = '/'): string | undefined {
    return this.retrieveImpl(key, context);
  }
}
